import {
  ROLE_COLUMNS,
  STATUS_CHANGES_ROWS,
  isStatusChangesCellWritable,
  type NotificationRole,
  type NotificationSpecifics,
  type StatusChangesKey,
} from '~/helpers/notificationPrefs';
import {
  aggregateSelectState,
  statusChangesColumnValues,
  statusChangesSectionValues,
} from '~/helpers/notificationPrefsSelectAll';
import { PrefCheckbox } from './PrefCheckbox';
import { ScrollablePrefsGrid } from './ScrollablePrefsGrid';
import { SectionSelectAll, toCheckboxState } from './SectionSelectAll';

type Props = {
  statusChanges: NotificationSpecifics['statusChanges'];
  disabled?: boolean;
  onToggle: (
    status: StatusChangesKey,
    role: NotificationRole,
    next: boolean
  ) => void;
  onSectionSelectAll: (checked: boolean) => void;
  onColumnSelectAll: (role: NotificationRole, checked: boolean) => void;
};

export const StatusChangesSpecifics = ({
  statusChanges,
  disabled,
  onToggle,
  onSectionSelectAll,
  onColumnSelectAll,
}: Props) => {
  const sectionState = aggregateSelectState(
    statusChangesSectionValues(statusChanges)
  );

  return (
    <section className='flex min-w-0 flex-col gap-3'>
      <h2 className='text-lg font-semibold text-primary'>Status Changes</h2>
      <SectionSelectAll
        state={sectionState}
        disabled={disabled}
        aria-label='Status Changes Select All'
        onChange={onSectionSelectAll}
      />
      <ScrollablePrefsGrid className='grid w-fit grid-cols-[minmax(7rem,auto)_repeat(3,minmax(5.5rem,auto))] items-center gap-x-6 gap-y-3'>
        <div />
        {ROLE_COLUMNS.map((col) => (
          <div
            key={col.key}
            className='max-w-[7rem] text-center text-xs font-semibold leading-snug text-primary'
          >
            {col.label}
          </div>
        ))}

        <div className='text-sm text-primary'>Select All</div>
        {ROLE_COLUMNS.map((col) => {
          const colState = aggregateSelectState(
            statusChangesColumnValues(statusChanges, col.key)
          );
          return (
            <PrefCheckbox
              key={col.key}
              state={toCheckboxState(colState)}
              aria-label={`Status Changes Select All — ${col.label}`}
              disabled={disabled}
              onChange={(next) => onColumnSelectAll(col.key, next)}
            />
          );
        })}

        {STATUS_CHANGES_ROWS.map((row) => (
          <div key={row.key} className='contents'>
            <div className='text-sm text-primary'>{row.label}</div>
            {ROLE_COLUMNS.map((col) =>
              isStatusChangesCellWritable(row.key, col.key) ? (
                <PrefCheckbox
                  key={col.key}
                  aria-label={`Status Changes — ${row.label} — ${col.label}`}
                  checked={Boolean(statusChanges[row.key]?.[col.key])}
                  disabled={disabled}
                  onChange={(next) => onToggle(row.key, col.key, next)}
                />
              ) : (
                <div key={col.key} aria-hidden='true' />
              )
            )}
          </div>
        ))}
      </ScrollablePrefsGrid>
    </section>
  );
};
